import React, { FC, useState } from 'react';
import { useTheme } from 'styled-components';

interface SystemInfoProps {
  servicesData: {
    celery: boolean;
    operations: boolean;
    updated: boolean;
  };
  appStatsData: Array<{
    id: number;
    details: any;
    last_updated: string;
    status_type: string;
  }>;
}

const SystemInfo: FC<SystemInfoProps> = ({ servicesData, appStatsData }) => {
  const [hoveredStatId, setHoveredStatId] = useState<number | null>(null);
  const theme: any = useTheme();
  const isDark = Boolean(theme?.isDark);
  const palette = theme?.palette ?? {};
  const colors = {
    cardBg: palette[10] ?? '#ffffff',
    cardBorder: palette[300] ?? '#e2e8f0',
    cardShadow: isDark ? '0 1px 3px rgba(15, 23, 42, 0.35)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
    heading: palette[850] ?? '#0f172a',
    secondaryText: palette[700] ?? '#64748b',
    metaText: palette[600] ?? '#94a3b8',
    surfaceMuted: palette[20] ?? '#f8fafc',
    processCardBg: palette[10] ?? '#ffffff',
    chipNeutralBg: palette[20] ?? '#f1f5f9',
    chipNeutralText: palette[850] ?? '#0f172a',
    chipInfoBg: isDark ? 'rgba(70, 113, 246, 0.18)' : '#dbeafe',
    chipInfoText: isDark ? theme.primary[400] : '#1d4ed8',
    chipSuccessBg: isDark ? 'rgba(16, 185, 129, 0.18)' : '#ecfdf5',
    chipErrorBg: isDark ? 'rgba(248, 113, 113, 0.18)' : '#fef2f2',
    healthSuccessBg: isDark ? 'rgba(16, 185, 129, 0.18)' : '#ecfdf5',
    healthWarnBg: isDark ? 'rgba(251, 191, 36, 0.2)' : '#fef3c7'
  };
  const processStatusColors = {
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
  };

  const getProcessStatusVariant = (lastUpdated: string): 'success' | 'warning' | 'danger' => {
    const date = new Date(lastUpdated);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (Number.isNaN(diffInMinutes)) return 'warning';
    if (diffInMinutes < 60) return 'success';
    if (diffInMinutes < 1440) return 'warning';
    return 'danger';
  };

  const services = [
    {
      name: 'Celery',
      status: servicesData.celery,
      description: 'Background task processing'
    },
    {
      name: 'Operations',
      status: servicesData.operations,
      description: 'System operations service'
    },
    {
      name: 'Updated',
      status: servicesData.updated,
      description: 'Update service status'
    }
  ];

  const activeServices = Object.values(servicesData).filter(Boolean).length;
  const totalServices = Object.keys(servicesData).length;

  // Helper function to format status type for display
  const formatStatusType = (statusType: string) => {
    return statusType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace(/Last Run/g, '')
      .trim();
  };

  // Helper function to format last updated time
  const formatLastUpdated = (lastUpdated: string) => {
    const date = new Date(lastUpdated);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const formatTooltipDate = (lastUpdated: string) => {
    const date = new Date(lastUpdated);
    if (Number.isNaN(date.getTime())) {
      return lastUpdated;
    }
    return date.toLocaleString();
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: colors.cardBg,
    borderRadius: '8px',
    padding: '20px',
    boxShadow: colors.cardShadow,
    border: `1px solid ${colors.cardBorder}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  };
  const tooltipContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
  };
  const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: isDark ? theme.palette[300] : '#0f172a',
    color: isDark ? theme.palette[850] : '#f8fafc',
    padding: '6px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.45)' : '0 4px 12px rgba(15, 23, 42, 0.16)',
    zIndex: 20,
    pointerEvents: 'none',
    border: isDark ? `1px solid ${theme.palette[400]}` : undefined,
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ 
        color: colors.heading,
        fontSize: '16px',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        System Status
      </h2>
      
      <div style={{
        backgroundColor: colors.surfaceMuted,
        border: `1px solid ${colors.cardBorder}`,
        borderRadius: '8px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px'
      }}>
        <div style={{ 
          fontSize: '28px', 
          fontWeight: '700', 
          color: activeServices === totalServices ? '#0f766e' : '#b45309'
        }}>
          {activeServices}/{totalServices}
        </div>
        <div style={{ 
          fontSize: '14px', 
          fontWeight: '500',
          color: colors.secondaryText
        }}>
          {activeServices === totalServices ? 'All Services Active' : 'Some Services Inactive'}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h4 style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          color: theme?.primary?.[400] ?? '#1d4ed8'
        }}>
          Core Services
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {services.map((service) => (
            <div
              key={service.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 14px',
                borderRadius: '0 4px 4px 0',
                border: `1px solid ${colors.cardBorder}`,
                backgroundColor: colors.surfaceMuted,
                borderLeft: `4px solid ${service.status ? '#10b981' : '#ef4444'}`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '9999px',
                  backgroundColor: service.status ? '#10b981' : '#ef4444'
                }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: colors.heading }}>
                    {service.name}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.secondaryText }}>
                    {service.description}
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: service.status ? '#10b981' : '#ef4444',
                backgroundColor: service.status ? colors.chipSuccessBg : colors.chipErrorBg,
                padding: '4px 10px',
                borderRadius: '9999px'
              }}>
                {service.status ? 'Active' : 'Inactive'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <h4 style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          color: theme?.primary?.[400] ?? '#1d4ed8'
        }}>
          System Processes
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {appStatsData?.map((stat) => {
            const statusVariant = getProcessStatusVariant(stat.last_updated);
            const statusColor = processStatusColors[statusVariant];
            return (
              <div
                key={stat.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px 14px',
                  borderRadius: '0 4px 4px 0',
                  border: '1px solid #e2e8f0',
                  backgroundColor: colors.processCardBg,
                  borderLeft: `4px solid ${statusColor}`
                }}
              >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: colors.heading
                  }}>
                    {formatStatusType(stat.status_type)}
                  </div>
                  {stat.details && (
                    <div style={{ fontSize: '12px', color: colors.secondaryText, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {stat.details.version && (
                        <div style={{ 
                          backgroundColor: colors.chipNeutralBg,
                          padding: '4px 8px',
                          borderRadius: '9999px',
                          color: colors.chipNeutralText,
                          fontWeight: 600
                        }}>
                          Version: {stat.details.version}
                        </div>
                      )}
                      {stat.details.updated_messages_count && (
                        <div style={{ 
                          backgroundColor: colors.chipInfoBg,
                          color: colors.chipInfoText,
                          padding: '4px 10px',
                          borderRadius: '9999px',
                          fontWeight: '600'
                        }}>
                          {stat.details.updated_messages_count.toLocaleString()} messages
                        </div>
                      )}
                      {stat.details.uptodate !== undefined && (
                        <div style={{ 
                          color: stat.details.uptodate ? '#10b981' : '#ef4444',
                          backgroundColor: stat.details.uptodate ? colors.chipSuccessBg : colors.chipErrorBg,
                          padding: '4px 10px',
                          borderRadius: '9999px',
                          fontWeight: '600'
                        }}>
                          {stat.details.uptodate ? 'Up to date' : 'Update available'}
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    style={{ 
                    ...tooltipContainerStyle,
                    fontSize: '12px', 
                    color: colors.metaText,
                    fontWeight: '500'
                  }}
                    onMouseEnter={() => setHoveredStatId(stat.id)}
                    onMouseLeave={() => setHoveredStatId(null)}
                  >
                    Last run: {formatLastUpdated(stat.last_updated)}
                    {hoveredStatId === stat.id && (
                      <div style={tooltipStyle}>
                        {formatTooltipDate(stat.last_updated)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{
        marginTop: '8px',
        padding: '12px 14px',
        borderRadius: '8px',
        border: `1px solid ${colors.cardBorder}`,
        backgroundColor: activeServices === totalServices ? colors.healthSuccessBg : colors.healthWarnBg,
        color: activeServices === totalServices ? '#047857' : '#b45309',
        fontSize: '12px',
        fontWeight: '600',
        textAlign: 'center'
      }}>
        System Health: {activeServices === totalServices ? 'Excellent' : 'Attention Required'}
      </div>
    </div>
  );
};

export default SystemInfo;
